import { ArrowLeft, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/shadcn/ui/button';
import { Switch } from '@/components/shadcn/ui/switch';
import {
  type SubscriptionFormData,
  subscriptionFormDefaultValues,
  subscriptionFormOptions,
  useCreateSubscriptionMutation,
  useUpdateSubscriptionMutation,
} from '@/entities/(notification)/subscription';
import { enableNotification } from '@/entities/notification';
import { errorHandler } from '@/error-handler';
import { SubscriptionFormContent } from '@/features/Workflow/subscription2';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import { useAppForm } from '@/shared/ui/form';
import { toast } from '@/stores/pure/ToastStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import {
  WORKFLOW_CHANNEL_TYPE,
  WORKFLOW_SUBSCRIPTION_BASIS,
  WORKFLOW_TYPE,
} from '@/types/workflowConst';

import useWorkflowDetail from '../useNotiDetail';

type Props = {
  initialState?: SubscriptionWorkflow;
  mode: EDIT_MODE;
};

const Subscription2WorkflowDetail = ({ initialState, mode }: Props) => {
  const wr = useWorkspaceRouter();
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const workspaceId = currWorkspace?.alias ?? '';
  const [enable, setEnable] = useState(initialState?.enable ?? true);

  const createMutation = useCreateSubscriptionMutation();
  const updateMutation = useUpdateSubscriptionMutation();

  const { onDelete } = useWorkflowDetail({
    mode,
    alias: initialState?.alias,
    initChange() {
      // no-op
    },
  });

  // Workflow 데이터를 form 데이터로 변환
  const getDefaultValues = (): SubscriptionFormData => {
    if (!initialState) return subscriptionFormDefaultValues;

    const { subscription, channel } = initialState;
    // WorkflowChannel을 form schema의 channel 타입으로 변환
    const formChannel: SubscriptionFormData['channel'] =
      channel?.type === WORKFLOW_CHANNEL_TYPE.SLACK
        ? {
            type: WORKFLOW_CHANNEL_TYPE.SLACK,
            slack_webhook_id: channel.slack_webhook_id ?? '',
          }
        : channel?.type === WORKFLOW_CHANNEL_TYPE.MAIL
          ? {
              type: WORKFLOW_CHANNEL_TYPE.MAIL,
              mail_address: channel.mail_address ?? '',
            }
          : { type: WORKFLOW_CHANNEL_TYPE.SLACK, slack_webhook_id: '' };

    return {
      title: initialState.title,
      channel: formChannel,
      basis: WORKFLOW_SUBSCRIPTION_BASIS.DASHBOARD2,
      target: subscription.target,
      schedule: subscription.schedule,
      timezone: subscription.timezone,
    };
  };

  // 저장 핸들러
  const handleSave = async (values: SubscriptionFormData) => {
    const payload = {
      title: values.title,
      enable,
      type: WORKFLOW_TYPE.SUBSCRIPTION,
      channel: values.channel,
      subscription: {
        basis: values.basis,
        target: values.target,
        schedule: {
          ...values.schedule,
          weekday: values.schedule.weekday ?? 0,
          date: values.schedule.date ?? 1,
        },
        timezone: values.timezone,
      },
    };

    if (mode === 'CREATE') {
      createMutation.mutate(payload, {
        onSuccess: () => {
          toast('Workflow created', 'success');
          wr.push('/workflow');
        },
        onError: (error) => {
          toast(errorHandler(error, 'Failed to create workflow'), 'fail');
        },
      });
    } else {
      updateMutation.mutate(
        {
          notification_id: initialState!.alias,
          ...payload,
        },
        {
          onSuccess: () => {
            toast('Workflow updated', 'success');
            wr.push('/workflow');
          },
          onError: (error) => {
            toast(errorHandler(error, 'Failed to update workflow'), 'fail');
          },
        },
      );
    }
  };

  const form = useAppForm({
    ...subscriptionFormOptions,
    defaultValues: getDefaultValues(),
    onSubmit: async ({ value }) => {
      await handleSave(value);
    },
  });

  // 토글 핸들러
  const handleToggle = async (status: boolean) => {
    if (!initialState) return;
    try {
      await enableNotification({
        notification_id: initialState.alias,
        enable: status,
      });
      setEnable(status);
      toast(status ? 'Workflow enabled' : 'Workflow disabled', 'success');
    } catch (error) {
      toast(errorHandler(error, 'Failed to toggle workflow'), 'fail');
    }
  };

  return (
    <div className="flex h-screen flex-col overflow-hidden max-w-[1400px]">
      {/* 헤더 */}
      <div className="flex items-center justify-between px-4 py-2 mb-6">
        <Button
          variant="outline"
          size="icon"
          onClick={() => wr.push('/workflow')}
        >
          <ArrowLeft className="size-4" />
        </Button>
        <div className="flex items-center gap-6">
          {mode === 'EDIT' && (
            <>
              <Switch checked={enable} onCheckedChange={handleToggle} />
              <Button variant="ghost" size="icon" onClick={onDelete}>
                <Trash2 className="size-4" />
              </Button>
            </>
          )}
        </div>
      </div>

      {/* 본문 */}
      <SubscriptionFormContent
        form={form}
        workspaceId={workspaceId}
        renderFooter={() => (
          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
          >
            {([canSubmit, isSubmitting]) => (
              <div className="flex gap-2">
                <Button
                  className="w-[100px]"
                  onClick={form.handleSubmit}
                  disabled={!canSubmit || isSubmitting}
                >
                  Save
                </Button>
              </div>
            )}
          </form.Subscribe>
        )}
      />
    </div>
  );
};

export default Subscription2WorkflowDetail;
